import React, { useMemo, useState } from "react";
import { CpButton, CpCard } from "@components";
import { useForm, useWatch } from "react-hook-form";
import { SortController } from "../common/sort/sort-controller.component";
import { InputConditionController } from "./input-condition-controller.component";
import {
  conditionOptsByDataType,
  ConditionOption,
} from "./input-filter.helper";
import { FilterFooter } from "../common/filter-footer.component";
import { isEmpty } from "lodash";
import {
  ColumnSchema,
  FilterControl,
  SortDirection,
} from "src/components/cp-data-table/types";
import { SelectDataItem } from "src/components/cp-select/common/utils";

type FormCondition = {
  condition: SelectDataItem | null | undefined;
  filterValue?: string;
  operator?: string;
};

type FormData = {
  sort: SortDirection | null;
  conditions: {
    [key: string]: FormCondition;
  };
};

export function InputFilter({
  columnSchema,
  onClose,
  filterControl,
  hideConditionOptions,
  hideSecondaryCondition,
  conditions,
  maxConditions = 2,
}: {
  columnSchema: ColumnSchema;
  onClose: () => void;
  filterControl: FilterControl;
  hideConditionOptions: string[];
  hideSecondaryCondition: boolean;
  conditions: ConditionOption[];
  maxConditions?: number;
}) {
  const filterData = filterControl.filters[columnSchema.id]?.data;
  const [extraConditions, setExtraConditions] = useState(
    () => filterData?.slice(1).map((data, i) => `conditions.${i + 1}`) || [],
  );
  const conditionOptions = useMemo(() => {
    const opts =
      conditions ||
      conditionOptsByDataType[columnSchema.dataType] ||
      conditionOptsByDataType.string;
    if (isEmpty(hideConditionOptions)) {
      return opts;
    } else {
      return opts.filter((o) => !hideConditionOptions.includes(o.id));
    }
  }, [hideConditionOptions, columnSchema, conditions]);

  const { handleSubmit, control, reset } = useForm<FormData>({
    defaultValues: {
      sort: filterControl.sortData[columnSchema.id]?.direction,
      conditions: filterData?.reduce(
        (acc, data, i) => {
          acc[String(i)] = {
            condition:
              getConditionValueById(data?.condition) || conditionOptions[0],
            filterValue: data?.filterValue,
            ...(data?.operator ? { operator: data?.operator } : {}),
          };
          return acc;
        },
        {} as FormData["conditions"],
      ) || {
        0: {
          condition: conditionOptions[0],
          filterValue: undefined,
        },
      },
    },
  });

  const globalOperator = useWatch({
    control,
    name: "conditions.1.operator",
  });

  function getConditionValueById(conditionId: string | undefined) {
    if (!conditionId) return null;
    return (
      conditionOptions.find((c) => c.id === conditionId) || {
        id: conditionId,
        name: conditionId,
      }
    );
  }

  function onSubmit(data: FormData) {
    const filterData = Object.keys(data.conditions)
      .map((i) => {
        const conditionData = data.conditions[i];
        if (!conditionData) return null;

        const conditionIndex = Number(i);
        const { operator, filterValue, condition } = conditionData;
        const trimmedValue = condition?.disableInput ? "" : filterValue?.trim();
        if (!condition || (!trimmedValue && !condition?.disableInput))
          return null;
        const effectiveOperator =
          maxConditions > 2 && conditionIndex > 0 ? globalOperator : operator;

        return {
          filterValue: trimmedValue,
          condition: condition.id,
          ...(effectiveOperator ? { operator: effectiveOperator } : {}),
        };
      })
      .filter((d) => !!d);
    filterControl.applyFilter(columnSchema.id, {
      filterData,
      sortDir: data.sort || null,
    });
    onClose();
  }

  function onClear() {
    reset({
      sort: null,
      conditions: {
        0: {
          condition: conditionOptions[0],
        },
      },
    });
    setExtraConditions([]);
  }

  return (
    <CpCard>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CpCard.Body className="max-h-[70vh] overflow-y-auto">
          {columnSchema.sort !== false && (
            <>
              <SortController
                control={control}
                dataType={columnSchema.dataType}
                ascLabel={columnSchema.sort.ascLabel}
                descLabel={columnSchema.sort.descLabel}
              />
              <div className="cp-divider" />
            </>
          )}
          <InputConditionController
            conditionOptions={conditionOptions}
            control={control}
            dataType={columnSchema.dataType}
            name="conditions.0"
          />
          {extraConditions.length > 0 && (
            <div className="flex flex-col cp-mt-16 cp-gap-16">
              {extraConditions.map((name, index) => {
                const conditionIndex = index + 1;
                const isSecondCondition = conditionIndex === 1;

                return (
                  <InputConditionController
                    conditionOptions={conditionOptions}
                    control={control}
                    dataType={columnSchema.dataType}
                    key={name}
                    name={name}
                    onRemove={() =>
                      setExtraConditions((val) => val.filter((n) => n !== name))
                    }
                    requireOperator={isSecondCondition}
                    showOperatorAsText={maxConditions > 2 && !isSecondCondition}
                    operatorText={globalOperator === "and" ? "And" : "Or"}
                  />
                );
              })}
            </div>
          )}
          {extraConditions.length + 1 < maxConditions &&
            !hideSecondaryCondition && (
              <CpButton
                aria-label="Add condition"
                btnType="tertiary"
                className="cp-mt-16"
                icon="add-small"
                onClick={() => {
                  if (extraConditions.length + 1 < maxConditions) {
                    setExtraConditions((val) => [
                      ...val,
                      "conditions." + (val.length + 1),
                    ]);
                  }
                }}
                type="button"
              >
                Add condition
              </CpButton>
            )}
        </CpCard.Body>
        <FilterFooter onCancel={onClose} onClear={onClear} />
      </form>
    </CpCard>
  );
}
